import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { StatTile, Panel, NeedsAttention, StatusPill, LivePill, EmptyState } from "@/components/lux";
import {
  Wrench, CheckCircle2, DollarSign, AlertTriangle, TrendingUp, Package, Receipt, Clock, Warehouse,
} from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const TOOLTIP_STYLE = {
  contentStyle: { background: "#121216", border: "1px solid #24242a", borderRadius: 12, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" },
};

const STATUS_TONE = {
  Estimate: "neutral", Approved: "blue", "In Progress": "amber", "Waiting on Parts": "violet",
  "Awaiting Parts": "violet", Ready: "green", "Picked Up": "green", Closed: "green",
  Completed: "green", Open: "amber",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["mech-dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const d = data || {};
  const lifts = d.lifts || {};

  const attention = [
    { key: "unpaid", label: "Invoices unpaid", count: d.unpaid_invoices || 0, tone: "red", to: "/mechanic/invoices" },
    { key: "waiting", label: "Jobs waiting on parts", count: d.waiting_parts || 0, tone: "amber", to: "/mechanic/work-orders" },
    { key: "ready", label: "Ready for pickup", count: d.ready_for_pickup || 0, tone: "green", to: "/mechanic/work-orders" },
    { key: "low-stock", label: "Parts low on stock", count: d.low_stock_count || 0, tone: "amber", to: "/mechanic/inventory" },
    { key: "needs", label: "Fleet cars needing service", count: d.cars_needing_service || 0, tone: "red", to: "/mechanic/rental-fleet" },
  ];

  return (
    <div className="p-6 sm:p-8" data-testid="mech-dashboard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl">01 Auto Care</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {d.in_progress ?? 0} jobs on the floor · {d.ready_for_pickup ?? 0} ready for pickup · live shop status
          </p>
        </div>
        <LivePill />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 stagger">
        <StatTile label="Revenue today" value={money(d.revenue_today || 0)} gold hint="external only" icon={DollarSign} tone="gold" testid="kpi-rev-today" />
        <StatTile label="Internal rev today" value={money(d.revenue_today_internal || 0)} gold hint="incl. dealer + rental" icon={DollarSign} tone="neutral" testid="kpi-rev-today-internal" />
        <StatTile label="Revenue MTD" value={money(d.month_revenue || 0)} gold hint="external only" icon={TrendingUp} tone="blue" testid="kpi-revenue" />
        <StatTile label="Internal rev MTD" value={money(d.month_revenue_internal || 0)} gold hint="incl. dealer + rental" icon={TrendingUp} tone="violet" testid="kpi-revenue-internal" />
        <StatTile label="Job costs MTD" value={money(d.month_costs || 0)} gold hint="parts + labour" icon={Wrench} tone="amber" testid="kpi-costs" />
        <StatTile label="Profit MTD" value={money(d.month_profit || 0)} gold hint="customer jobs" icon={CheckCircle2} tone="green" testid="kpi-profit" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8 stagger">
        <StatTile label="Unpaid invoices" value={money(d.unpaid_total || 0)} gold hint={`${d.unpaid_invoices ?? 0} open`} icon={Receipt} tone="red" testid="kpi-unpaid" />
        <StatTile label="In progress" value={d.in_progress ?? "—"} hint="on the lifts & floor" icon={Wrench} tone="amber" testid="kpi-in-progress" />
        <StatTile label="Waiting on parts" value={d.waiting_parts ?? "—"} hint="blocked jobs" icon={Clock} tone="violet" testid="kpi-waiting" />
        <StatTile label="Ready for pickup" value={d.ready_for_pickup ?? "—"} hint="call the customer" icon={CheckCircle2} tone="green" testid="kpi-ready" />
        <StatTile label="Low stock items" value={d.low_stock_count ?? "—"} hint="reorder soon" icon={Package} tone="amber" testid="kpi-low-stock" />
        <StatTile label="Cars needing service" value={d.cars_needing_service ?? "—"} hint="rental fleet" icon={AlertTriangle} tone="red" testid="kpi-needs" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Panel label="Trend" title="Revenue vs costs (6 months)" className="lg:col-span-2">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="month" fontSize={11} stroke="#71717A" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#71717A" tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => money(v)} {...TOOLTIP_STYLE} />
                <Bar dataKey="revenue" name="Revenue (external)" fill="#d6bb84" radius={[5, 5, 0, 0]} maxBarSize={26} />
                <Bar dataKey="revenue_internal" name="Internal Revenue" fill="#7dd3fc" radius={[5, 5, 0, 0]} maxBarSize={26} />
                <Bar dataKey="cost" name="Costs" fill="#52525b" radius={[5, 5, 0, 0]} maxBarSize={26} />
                <Line dataKey="profit" name="Profit" stroke="#4ade80" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel
          label="Bays"
          title="Lifts"
          icon={Warehouse}
          actions={<Link to="/mechanic/shop-view" className="text-xs text-primary hover:underline" data-testid="dash-shop-view-link">Shop view →</Link>}
        >
          <div className="space-y-2.5">
            {[1, 2, 3].map((n) => {
              const jobs = lifts[String(n)] || [];
              return (
                <div key={n} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`dash-lift-${n}`}>
                  <span className="font-heading text-xl text-primary w-5 shrink-0">{n}</span>
                  {jobs.length === 0 ? (
                    <span className="text-sm text-muted-foreground">Empty</span>
                  ) : (
                    <div className="min-w-0 space-y-1">
                      {jobs.map((j) => (
                        <p key={j.id} className="text-sm truncate flex items-center gap-2">
                          <span className="truncate">{j.label}</span>
                          <StatusPill status={j.status} tone={STATUS_TONE[j.status] || "neutral"}>{j.status}</StatusPill>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <NeedsAttention items={attention} testid="mech-needs-attention" />

        <Panel label="Latest activity" title="Recent work orders" flush className="lg:col-span-2">
          {(d.recent || []).length === 0 ? (
            <EmptyState icon={Wrench} title="No work orders yet" hint="Create a work order to see it here." />
          ) : (
            <div className="divide-y divide-border/60">
              {(d.recent || []).map((w) => (
                <button key={w.id} className="w-full px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-white/[0.03] transition-colors text-left"
                  onClick={() => navigate(`/mechanic/vehicle/${w.vehicle_source}/${w.vehicle_ref_id}`)}>
                  <div className="min-w-0">
                    <p className="font-heading text-base truncate">{w.vehicle_label || "Vehicle"}</p>
                    <p className="text-xs text-muted-foreground truncate">{w.complaint || "Service"} · {fmtDate(w.service_date)} · {w.vehicle_source}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatusPill status={w.status} tone={STATUS_TONE[w.status] || "neutral"}>{w.status}</StatusPill>
                    <span className="font-heading text-base money">{money(w.totals?.total)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
